// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  loadAccountDetailKeyRequests,
  exportAccountDetailKeyRequests,
} from '../../../actions/accountDetailActions';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { Link } from 'react-router-dom';
import FormattedDate from '../../common/FormattedDate';
import KebabMenu from '../../common/KebabMenu';
import Button from '../../common/Button';
import Checkbox from '../../common/Checkbox';
import icons from '../../../constants/icons';
import { useTranslation } from 'react-i18next';

export interface AccountKeysListProps {
  currentUser?: any;
  reload?: any;
  setBreadcrumb: (crumb: any) => void;
  tableViewSwitcher?: React.ReactNode;
  itemsChecked: any[];
  setItemsChecked: (items: any[]) => void;
  onDisableKeys: (items: any[]) => void;
}

function AccountKeysList({
  reload,
  setBreadcrumb,
  tableViewSwitcher,
  itemsChecked,
  setItemsChecked,
  onDisableKeys,
}: AccountKeysListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const allColumns = [
    'login',
    'name',
    'emailAddress',
    'game',
    'sku',
    'keyRequestReference',
    'emailSentDate',
    'keyCodeDownloadedDate',
  ];
  const suffixColumns = ['buttons-checkbox'];
  const defaultVisibleColumns = allColumns;
  const [accountDetails, setAccountDetails] = useState<any>();
  const params = new URLSearchParams(location.search);

  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'account-keys-settings',
      getTableSettingsDefaults({
        defaultSort: 'login',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      null,
      loadAccountDetailKeyRequests,
      setAccountDetails,
      reload,
      t('Failed to load account details')
    );

  const columnsConfig = [
    {
      Key: 'login',
      Label: t('Login'),
      Render: (item: any) => (
        <NavLink to={`/keys/account/${item.accountDetailId}`}>
          {item.login}
        </NavLink>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'name',
      Label: t('Name'),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailAddress',
      Label: t('Email address'),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'game',
      Label: t('Game'),
      Render: (item: any) => (
        <>
          <NavLink to={`/sku/${item.skuId}`} className="game-title">
            {item.game}
          </NavLink>
          <br />
          <NavLink to={`/sku/${item.skuId}`}>{item.sku}</NavLink>
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyRequestReference',
      Label: t('Key request'),
      Render: (item: any) => (
        <Link to={'/request/' + item.keyRequestGroupId}>
          {item.keyRequestReference}
        </Link>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'emailSentDate',
      Label: t('Sent date'),
      Render: (item: any) => <FormattedDate value={item.emailSentDate} variant="default" />,
    },
    {
      Key: 'keyCodeDownloadedDate',
      Label: t('Downloaded date'),
      Render: (item: any) => <FormattedDate value={item.keyCodeDownloadedDate} variant="default" />,
    },
    {
      Key: 'buttons-checkbox',
      Label: '',
      className: 'text-right no-navigate',
      Sort: false,
      width: '2%',
      Filter: {
        Render: (col: any) => (
          <Checkbox
            disabled={!accountDetails}
            checked={false}
            onChange={(checked) =>
              checked
                ? setItemsChecked(accountDetails?.data ?? [])
                : setItemsChecked([])
            }
          />
        ),
      },
      Render: (item: any) => (
        <div className="d-flex align-items-center justify-content-end">
          <KebabMenu
            take={1}
            className="inline-icons"
            buttons={[
              <Button
                key={item.id}
                className="btn btn-danger btn-xs-full me-2"
                dropdownclasses="danger"
                onClick={() => onDisableKeys([item])}
                icon={icons.DISABLE_KEYS}
                text={t('Disable keys')}
              />,
            ]}
          />
          <Checkbox
            className="ms-3"
            checked={itemsChecked?.some((x) => x.id === item.id)}
            onChange={(checked) =>
              checked
                ? setItemsChecked([...itemsChecked, item])
                : setItemsChecked(itemsChecked.filter((x) => x.id !== item.id))
            }
          />
        </div>
      ),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return accountDetails ? (
    <KwPanel className="mb-4">
      <header>
        <h2>{t('Accounts - Ungrouped')}</h2>
        {accountDetails.data.length > 0 && (
          <Button
            className="btn btn-danger btn-xs-full space-bottom-xs-s me-2"
            onClick={() => onDisableKeys(itemsChecked)}
            icon={icons.DISABLE_KEYS}
            text={t('Disable selected keys')}
            isDisabled={itemsChecked.length === 0}
          />
        )}
      </header>
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={accountDetails.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={accountDetails.totalRowCount}
        filteredRowCount={accountDetails.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportAccountDetailKeyRequests}
        tableActionBarButtons={tableViewSwitcher}
        scopes={accountDetails.scopes}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}
export default AccountKeysList;
